import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Modal width at or below which the task and its activity are shown one at a time (see task.css). */
const NARROW_MODAL = 820;
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  ChevronDown,
  ChevronUp,
  CircleDot,
  Copy,
  Ellipsis,
  Folder,
  ListTodo,
  Maximize2,
  Minimize2,
  PanelRightClose,
  PanelRightOpen,
  Star,
  Trash2,
  X,
  Archive,
  CopyPlus,
  Hash,
} from '@/components/ui/icons';
import { useDb, useDbShallow, useEntity, useMe } from '@/lib/store';
import { statusOf, taskPath } from '@/lib/hierarchy';
import { locationPath, useOpenTask, useWorkspaceId } from '@/lib/nav';
import { archiveTask, deleteTask, duplicateTask, toggleFavorite, updateTask } from '@/lib/actions';
import { pushRecent } from '@/lib/ui';
import { formatDateLong } from '@/lib/format';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuSeparator } from '@/components/ui/Menu';
import { Tooltip } from '@/components/ui/Tooltip';
import { toast } from '@/components/ui/overlays';
import { Icon } from '@/components/ui/Icon';
import { TaskTypeMenu } from '@/components/fields/TaskCells';
import { SpaceAvatar } from '@/shell/WorkspaceLogo';
import { MoveTaskMenu } from '@/views/MoveTaskMenu';
import { TaskProperties } from './TaskProperties';
import { TaskDescription } from './TaskDescription';
import { TaskFields } from './TaskFields';
import { SubtasksSection } from './SubtasksSection';
import { ChecklistsSection } from './ChecklistsSection';
import { RelationshipsSection } from './RelationshipsSection';
import { AttachmentsSection } from './AttachmentsSection';
import { ActivityPanel } from './ActivityPanel';
import { useTaskSiblings } from './useTaskSiblings';
import { ShareButton } from '@/components/ShareButton';

function TitleEditor({ taskId, name }: { taskId: string; name: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(name);
  useEffect(() => setValue(name), [name]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  const commit = () => {
    const v = value.replace(/\s+/g, ' ').trim();
    if (v && v !== name) updateTask(taskId, { name: v });
    else setValue(name);
  };
  return (
    <textarea
      ref={ref}
      className="tm-title"
      value={value}
      rows={1}
      spellCheck={false}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLTextAreaElement).blur();
        }
        if (e.key === 'Escape') {
          setValue(name);
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
    />
  );
}

export function TaskModal({ taskId }: { taskId: string }) {
  const task = useEntity('tasks', taskId);
  const openTask = useOpenTask();
  const navigate = useNavigate();
  const ws = useWorkspaceId();
  const me = useMe();
  const db = useDbShallow((d) => ({ lists: d.lists, folders: d.folders, spaces: d.spaces, taskTypes: d.taskTypes, favorites: d.favorites }));
  const parents = useDbShallow((d) => (task ? taskPath(d, task) : []));
  // A narrow modal (≤ NARROW_MODAL px) shows the task or its activity, not both, and opens on the task;
  // a wide one shows both. Once toggled, the choice holds for this modal.
  const [narrow, setNarrow] = useState(false);
  // the activity slide only animates once the modal has measured itself (no slide while it settles on open)
  const [settled, setSettled] = useState(false);
  const [activityPref, setActivityPref] = useState<boolean | null>(null);
  const showActivity = activityPref ?? !narrow;
  const setShowActivity = (v: boolean) => setActivityPref(v);
  const sizeObserver = useRef<ResizeObserver | null>(null);
  const modalRef = useCallback((el: HTMLDivElement | null) => {
    sizeObserver.current?.disconnect();
    if (!el) return;
    setNarrow(el.getBoundingClientRect().width <= NARROW_MODAL);
    sizeObserver.current = new ResizeObserver(([entry]) => setNarrow(entry.contentRect.width <= NARROW_MODAL));
    sizeObserver.current.observe(el);
    setTimeout(() => setSettled(true), 60);
  }, []);
  const [fullscreen, setFullscreen] = useState(false);
  const { prev, next } = useTaskSiblings(task);
  const close = () => openTask(null);

  useEffect(() => {
    if (task) pushRecent({ kind: 'task', id: task.id });
  }, [task?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      const typing = el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA';
      if (e.key === 'Escape' && !typing && !document.querySelector('.popover')) close();
      if (!typing && e.key === 'k' && prev) openTask(prev.id, { replace: true });
      if (!typing && e.key === 'j' && next) openTask(next.id, { replace: true });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const list = task ? db.lists[task.listId] : undefined;
  const folder = list?.folderId ? db.folders[list.folderId] : undefined;
  const space = list ? db.spaces[list.spaceId] : undefined;
  const isFav = useMemo(() => Object.values(db.favorites).some((f) => f.userId === me && f.target.type === 'task' && f.target.id === taskId), [db.favorites, me, taskId]);
  const taskType = task?.taskTypeId ? db.taskTypes[task.taskTypeId] : Object.values(db.taskTypes).find((t) => t.builtin === 'task');

  if (!task) {
    return createPortal(
      <div className="tm-overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
        <div className="tm" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <div className="page-placeholder">
            <h2>Task not found</h2>
            <p>It may have been deleted or you don't have access.</p>
            <button className="btn outline" onClick={close}>
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  const copyLink = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('t', task.id);
    void navigator.clipboard.writeText(url.toString());
    toast('Task link copied');
  };

  return createPortal(
    <div className="tm-overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div ref={modalRef} className={clsx('tm', { fullscreen })} role="dialog" aria-modal="true">
        <header className="tm-header">
          <div className="row gap-4 grow" style={{ minWidth: 0 }}>
            <Tooltip label="Previous task" shortcut="K">
              <button className="icon-btn sm" disabled={!prev} onClick={() => prev && openTask(prev.id, { replace: true })}>
                <ChevronUp size={16} />
              </button>
            </Tooltip>
            <Tooltip label="Next task" shortcut="J">
              <button className="icon-btn sm" disabled={!next} onClick={() => next && openTask(next.id, { replace: true })}>
                <ChevronDown size={16} />
              </button>
            </Tooltip>
            <span className="tm-crumbs">
              {space && (
                <button className="tm-crumb" onClick={() => navigate(locationPath(ws, { type: 'space', id: space.id }))}>
                  <SpaceAvatar spaceId={space.id} size={16} />
                  <span className="truncate">{space.name}</span>
                </button>
              )}
              {folder && (
                <>
                  <span className="loc-sep">/</span>
                  <button className="tm-crumb" onClick={() => navigate(locationPath(ws, { type: 'folder', id: folder.id }))}>
                    <Folder size={14} className="faint" />
                    <span className="truncate">{folder.name}</span>
                  </button>
                </>
              )}
              {list && (
                <>
                  <span className="loc-sep">/</span>
                  <Popover content={(c) => <MoveTaskMenu taskIds={[task.id]} onDone={c} />}>
                    <button className="tm-crumb current">
                      <ListTodo size={14} className="faint" />
                      <span className="truncate">{list.name}</span>
                    </button>
                  </Popover>
                </>
              )}
            </span>
          </div>
          <div className="row gap-4">
            <span className="faint tm-created">Created {formatDateLong(task.createdAt)}</span>
            <ShareButton
              target={{ type: 'task', id: task.id }}
              link={() => {
                const url = new URL(window.location.href);
                url.searchParams.set('t', task.id);
                return url.toString();
              }}
            />
            <Popover
              placement="bottom-end"
              content={(c) => (
                <Menu style={{ width: 220 }}>
                  <MenuItem icon={<Copy size={15} />} label="Copy link" onClick={() => (copyLink(), c())} />
                  <MenuItem icon={<Hash size={15} />} label="Copy ID" onClick={() => (void navigator.clipboard.writeText(task.id), toast('Task ID copied'), c())} />
                  <MenuItem
                    icon={<CopyPlus size={15} />}
                    label="Duplicate"
                    onClick={() => {
                      c();
                      const d = duplicateTask(task.id);
                      if (d) openTask(d.id);
                    }}
                  />
                  <MenuItem icon={<Icon name="ArrowRight" size={15} />} label="Move" submenu={(c2) => <MoveTaskMenu taskIds={[task.id]} onDone={() => (c2(), c())} />} />
                  <MenuSeparator />
                  <MenuItem
                    icon={<Archive size={15} />}
                    label={task.archived ? 'Unarchive' : 'Archive'}
                    onClick={() => {
                      archiveTask(task.id, !task.archived);
                      c();
                    }}
                  />
                  <MenuItem
                    icon={<Trash2 size={15} />}
                    label="Delete"
                    danger
                    onClick={() => {
                      c();
                      if (confirm(`Delete "${task.name}"?`)) {
                        deleteTask(task.id);
                        close();
                      }
                    }}
                  />
                </Menu>
              )}
            >
              <button className="icon-btn sm">
                <Ellipsis size={16} />
              </button>
            </Popover>
            <Tooltip label={isFav ? 'Remove from favorites' : 'Add to favorites'}>
              <button className="icon-btn sm" onClick={() => toggleFavorite({ type: 'task', id: task.id })}>
                <Star size={15} fill={isFav ? '#f8ae00' : 'none'} color={isFav ? '#f8ae00' : 'currentColor'} />
              </button>
            </Tooltip>
            <Tooltip label={fullscreen ? 'Exit full screen' : 'Full screen'}>
              <button className="icon-btn sm" onClick={() => setFullscreen(!fullscreen)}>
                {fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
              </button>
            </Tooltip>
            <Tooltip label={showActivity ? 'Hide activity' : 'Show activity'}>
              <button className="icon-btn sm" onClick={() => setShowActivity(!showActivity)}>
                {showActivity ? <PanelRightClose size={15} /> : <PanelRightOpen size={15} />}
              </button>
            </Tooltip>
            <Tooltip label="Close" shortcut="Esc">
              <button className="icon-btn sm" onClick={close}>
                <X size={17} />
              </button>
            </Tooltip>
          </div>
        </header>
        <div className={clsx('tm-body', { narrow, settled })}>
          <div className="tm-main">
            <div className="tm-content">
              <div className="tm-topline">
                <Popover
                  content={(c) => (
                    <TaskTypeMenu
                      workspaceId={task.workspaceId}
                      value={task.taskTypeId}
                      onPick={(id) => {
                        updateTask(task.id, { taskTypeId: id });
                        c();
                      }}
                    />
                  )}
                >
                  <button className="tm-type">
                    {taskType ? <Icon name={taskType.icon} size={14} color={taskType.color ?? undefined} /> : <CircleDot size={14} />}
                    {taskType?.name ?? 'Task'}
                    <ChevronDown size={13} className="faint" />
                  </button>
                </Popover>
                <Tooltip label="Copy task ID">
                  <button className="tm-id" onClick={() => (void navigator.clipboard.writeText(task.id), toast('Task ID copied'))}>
                    #{task.id}
                  </button>
                </Tooltip>
                {task.archived && <span className="chip">Archived</span>}
              </div>
              {parents.length > 0 && (
                <div className="tm-parent">
                  <span className="faint">Subtask of</span>
                  {parents.map((p, i) => (
                    <span key={p.id} className="row gap-4">
                      {i > 0 && <span className="faint">/</span>}
                      <button className="tm-parent-link" onClick={() => openTask(p.id)}>
                        <ParentStatusDot taskId={p.id} />
                        <span className="truncate">{p.name}</span>
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <TitleEditor taskId={task.id} name={task.name} />
              <TaskProperties task={task} />
              <TaskDescription task={task} />
              <TaskFields task={task} />
              <SubtasksSection task={task} />
              <ChecklistsSection task={task} />
              <RelationshipsSection task={task} />
              <AttachmentsSection task={task} />
            </div>
          </div>
          {/* stays mounted so it can slide in and out (task.css .tm-activity-slot) */}
          <div className={clsx('tm-activity-slot', { open: showActivity })} aria-hidden={!showActivity || undefined}>
            <ActivityPanel task={task} />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ParentStatusDot({ taskId }: { taskId: string }) {
  const color = useDb((d) => {
    const t = d.tasks[taskId];
    return t ? statusOf(d, t).color : '#87909e';
  });
  return <span className="dot" style={{ background: color }} />;
}

